"use client";

import { useSyncExternalStore } from "react";
import Link from "next/link";
import { GADS_ID, denyConsent, grantConsent, onConsentChange, readConsent } from "@/lib/gtag";

/**
 * Consent banner for advertising measurement (Consent Mode v2 gate).
 * Only rendered when the Google tag is configured and no choice is stored;
 * declining keeps the tag in its all-denied default state. The stored choice
 * is read as an external store — the server snapshot pretends a choice exists
 * so SSR/hydration never shows the banner, then the client read takes over.
 */
export function ConsentBanner() {
  const choice = useSyncExternalStore(onConsentChange, readConsent, () => "denied" as const);

  if (!GADS_ID || choice !== null) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-surface px-[var(--gutter)] py-4"
      style={{ boxShadow: "0 -8px 30px rgba(32,37,34,.10)" }}
    >
      <div className="mx-auto flex max-w-[var(--maxw)] flex-wrap items-center gap-x-6 gap-y-3">
        <p className="min-w-[240px] flex-1 text-[13.5px] leading-relaxed text-ink-3">
          May we use a Google cookie to measure whether our ads work? No ad
          personalization, no data sold — just counting signups.{" "}
          <Link href="/privacy" className="font-semibold text-green hover:text-copper">
            Details in our privacy policy
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-2.5">
          <button
            type="button"
            onClick={denyConsent}
            className="inline-flex h-[38px] items-center rounded-[12px] border border-border bg-surface px-4 text-[13.5px] font-semibold text-ink-2"
          >
            No thanks
          </button>
          <button
            type="button"
            onClick={grantConsent}
            className="inline-flex h-[38px] items-center rounded-[12px] bg-green px-4 text-[13.5px] font-semibold text-surface"
          >
            That&apos;s fine
          </button>
        </div>
      </div>
    </div>
  );
}
